import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { router, useLocalSearchParams } from 'expo-router';
import { Bookmark, HeartIcon, Share2, MessageCircle } from 'lucide-react-native';
import { useCallback } from 'react';
import { Share, ViewStyle, type PressableStateCallbackType } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { useToggleLikedPost } from '../../hooks/useToggleLikedPost';
import { useToggleSavedPost } from '../../hooks/useToggleSavedPost';
import type { EngageableLibraryPost } from '../../model/types';

import { HStack } from '@/components/ui/hstack';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { COMMUNITY_SORT_OPTIONS, type SortOption } from '@/constants/community';
import { COMMUNITY_POST_SHARE, getCommunityPostMessageWithLink } from '@/constants/invites';
import { useAbilityGate } from '@/hooks/useAbilityGate';
import { useColors } from '@/hooks/useColors';
import { useHaptic } from '@/hooks/useHaptic';
import { useToaster } from '@/hooks/useToaster';
import { ICON_BUTTON_HIT_SLOP } from '@/util/accessibleTouchTarget';
import { trackCommunityPostShare } from '@/util/analyticsEvents';

type CommunityActionBarProps = {
  item: EngageableLibraryPost;
  style?: ViewStyle;
  className?: string;
  searchInput?: string;
  sortBy?: SortOption;
  cancerTypeId?: number;
  /**
   * Which controls to draw. The collapsed comment bar splits them: the comment
   * count reads as a destination and sits before the field, the reactions sit
   * after it. Unset draws the whole bar, which is what the feed wants.
   */
  only?: 'comments' | 'reactions';
  /**
   * The bar sits on the post's own page, which changes its wording and what the comments button does.
   * Passed by the post screen rather than read from the URL: going back to the feed leaves the
   * feed's cards on screen for a moment while the URL is still the post's, and they would borrow the
   * post page's wording until it changed.
   */
  onPostPage?: boolean;
};

export function CommunityActionBar({
  item,
  style,
  searchInput,
  sortBy,
  cancerTypeId,
  only,
  onPostPage = false,
}: CommunityActionBarProps) {
  const { colors } = useColors();

  const { q } = useLocalSearchParams<{ q?: string }>();

  const isCommunityPost = onPostPage;

  const { guard } = useAbilityGate();

  const commentCount = item.comments ?? 0;

  /**
   * Labeled on the post page, bare in the feed. The label names a destination,
   * so it is only worth the words when there is somewhere to go: a post with no
   * comments keeps the icon, which still opens an empty thread.
   */
  const postPageLabel = commentCount > 0 ? `${commentCount} comments` : '';

  const commentLabel = isCommunityPost ? postPageLabel : String(commentCount);

  /**
   * On the post page the control scrolls to the thread, so its name says so. It contains the
   * visible words, "N comments", so the label in the name still matches. In the feed the visible
   * count is the name's last word and the control opens the post.
   */
  const commentsAccessibilityLabel = isCommunityPost
    ? `Jump to ${postPageLabel || 'comments'}`
    : undefined;

  const likes = item.likes || 0;

  const liked = Boolean(item.liked);

  /**
   * Named as the action it will take, with the count the visible number shows, since a label
   * replaces the control's contents for a screen reader.
   */
  const likeAccessibilityLabel = `${liked ? 'Unlike' : 'Like'} inquiry, ${likes} ${likes === 1 ? 'like' : 'likes'}`;

  const toast = useToaster();

  const haptic = useHaptic();

  const { saved, toggleSaved } = useToggleSavedPost({
    item,
    cancerTypeId,
    sortBy,
    q: (q as string) || undefined,
  });

  const { toggleLiked } = useToggleLikedPost({
    item,
    cancerTypeId,
    sortBy,
    q: (q as string) || undefined,
    onFailed: ({ liked: requested }) => {
      toast.showError({
        title: requested ? 'Failed to Like Inquiry' : 'Failed to Unlike Inquiry',
        message: "We couldn't process your request. Please try again.",
      });
    },
  });

  const onShare = useCallback(async () => {
    try {
      trackCommunityPostShare({ postId: item.id, stage: 'started' });

      const postUrl = `${COMMUNITY_POST_SHARE.URL}/${item.id}`;

      const result = await Share.share(
        {
          message: getCommunityPostMessageWithLink(postUrl),
          title: COMMUNITY_POST_SHARE.TITLE,
        },
        {
          // Android only
          dialogTitle: COMMUNITY_POST_SHARE.TITLE,
        }
      );

      // NOTE: on web result is undefined and it will throw if it's canceled/dimissed
      if (isWeb || result.action === Share.sharedAction) {
        trackCommunityPostShare({ postId: item.id, stage: 'completed' });

        return;
      }

      if (result.action === Share.dismissedAction) {
        trackCommunityPostShare({ postId: item.id, stage: 'cancelled' });
      }
    } catch (error) {
      if (error instanceof Error && error.message === 'Share canceled') {
        trackCommunityPostShare({ postId: item.id, stage: 'cancelled' });

        return;
      }

      console.error('communityActionBar:share:error', error);

      toast.showError({
        title: 'Unable to share the link',
      });
    }
  }, []);

  return (
    <HStack className={twJoin('items-center', only ? 'gap-5' : 'justify-between')} style={style}>
      <HStack className="flex-row items-center gap-5">
        <Pressable
          hitSlop={ICON_BUTTON_HIT_SLOP}
          accessibilityRole="button"
          accessibilityLabel={likeAccessibilityLabel}
          className={twJoin(
            'min-h-10 min-w-10 flex-row items-center justify-start gap-1.5',
            only === 'comments' && 'hidden'
          )}
          onPress={() => {
            if (!guard(liked ? 'delete' : 'create', 'PostLike')) {
              return;
            }

            haptic.impactLight();

            toggleLiked();
          }}>
          {({ pressed }: PressableStateCallbackType) => (
            <>
              <HeartIcon
                opacity={pressed ? 0.5 : 1}
                color={colors.primary[600]}
                size={24}
                fill={liked ? colors.primary[600] : colors.transparent}
              />
              {/* The counts are dropped on the post page, where the bar is
                  pinned above the comment input and the same numbers are
                  already on screen. In the feed they are the only place a
                  reader sees them, so they stay. */}
              {isCommunityPost ? null : (
                <Text className={twJoin('ml-1', 'text-typography-600')}>{likes || 0}</Text>
              )}
            </>
          )}
        </Pressable>

        <Pressable
          hitSlop={ICON_BUTTON_HIT_SLOP}
          accessibilityRole="button"
          accessibilityLabel={commentsAccessibilityLabel}
          className={twJoin(
            'min-h-10 min-w-10 flex-row items-center justify-start gap-1.5',
            only === 'reactions' && 'hidden'
          )}
          onPress={() => {
            if (!guard('read', 'Post')) {
              return;
            }

            if (isCommunityPost) {
              router.setParams({ scrollTo: 'comments' });

              return;
            }

            const params = new URLSearchParams({
              scrollTo: 'comments',
            });

            if (searchInput) {
              params.set('q', searchInput);
            }

            if (sortBy && sortBy !== COMMUNITY_SORT_OPTIONS[0].value) {
              params.set('sortBy', sortBy);
            }

            if (cancerTypeId) {
              params.set('cancer-type-id', String(cancerTypeId));
            }

            router.push(`/community/${item.id}?${params.toString()}`);
          }}>
          {({ pressed }: PressableStateCallbackType) => (
            <>
              <MessageCircle opacity={pressed ? 0.5 : 1} color={colors.primary[600]} size={24} />
              {/* Labelled on the post page, bare in the feed. The collapsed
                  comment bar puts this beside the "Add your thoughts" prompt,
                  where a lone number reads as a count rather than as the way
                  to reach the thread. */}
              {/* The label names a destination, so it is only worth the words
                  when there is somewhere to go. A post with none keeps the
                  icon, which still opens an empty thread. */}
              <Text className={twJoin('ml-1', 'text-typography-600')}>{commentLabel}</Text>
            </>
          )}
        </Pressable>
      </HStack>

      <HStack className={twJoin('gap-5', only === 'comments' && 'hidden')}>
        <Pressable
          hitSlop={ICON_BUTTON_HIT_SLOP}
          accessibilityRole="button"
          accessibilityLabel="Share inquiry"
          className="min-h-10 min-w-10 flex-row items-center justify-center"
          onPress={onShare}>
          {({ pressed }: PressableStateCallbackType) => (
            <Share2
              opacity={pressed ? 0.5 : 1}
              color={colors.primary[600]}
              size={24}
              fill={pressed ? colors.primary[600] : colors.transparent}
            />
          )}
        </Pressable>

        <Pressable
          hitSlop={ICON_BUTTON_HIT_SLOP}
          accessibilityRole="button"
          accessibilityLabel={saved ? 'Remove from saved inquiries' : 'Save inquiry'}
          className="min-h-10 min-w-10 flex-row items-center justify-center"
          onPress={() => {
            if (!guard(saved ? 'delete' : 'create', 'PostSave')) {
              return;
            }

            haptic.impactLight();

            toggleSaved();
          }}>
          {({ pressed }: PressableStateCallbackType) => (
            <Bookmark
              opacity={pressed ? 0.5 : 1}
              color={colors.primary[600]}
              size={24}
              fill={saved ? colors.primary[600] : colors.transparent}
            />
          )}
        </Pressable>
      </HStack>
    </HStack>
  );
}
